import {
  AcademicCapIcon,
  ArrowRightIcon,
  BookOpenIcon,
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  ClipboardDocumentListIcon,
  CpuChipIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import {
  Badge,
  Button,
  Card,
  Container,
  Group,
  rem,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { Breadcrumb } from "../../components/common/Breadcrumb";

interface DictionaryCard {
  id: string;
  title: string;
  description: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;
  route: string;
  itemCount?: number;
  color: string;
}

const dictionaryCards: DictionaryCard[] = [
  {
    id: "certificates",
    title: "Certificates",
    description:
      "Manage professional certifications and qualifications used throughout CVs",
    icon: AcademicCapIcon,
    route: "/operations/dictionaries/certificates",
    color: "blue",
  },
  {
    id: "technologies",
    title: "Technologies & Categories",
    description:
      "Manage technologies, programming languages, and their categories",
    icon: CpuChipIcon,
    route: "/operations/dictionaries/technologies",
    color: "green",
  },
  {
    id: "education",
    title: "Education Dictionaries",
    description:
      "Manage educational institutions, faculties, degrees and proficiency levels",
    icon: BookOpenIcon,
    route: "/operations/dictionaries/education",
    color: "orange",
  },
  {
    id: "languages",
    title: "Language Dictionaries",
    description:
      "Manage languages and proficiency levels for multi-language CV support",
    icon: GlobeAltIcon,
    route: "/operations/dictionaries/languages",
    color: "purple",
  },
  {
    id: "roles",
    title: "Roles & Positions",
    description: "View standardized job roles and position titles used in CVs",
    icon: ClipboardDocumentListIcon,
    route: "/operations/dictionaries/roles",
    color: "teal",
  },
  {
    id: "companies",
    title: "Companies & Employers",
    description: "View companies and employers from work experience entries",
    icon: BuildingOfficeIcon,
    route: "/operations/dictionaries/companies",
    color: "indigo",
  },
  {
    id: "officelocations",
    title: "Office Locations",
    description: "Manage office locations and geographical information",
    icon: BuildingOffice2Icon,
    route: "/operations/dictionaries/officelocations",
    color: "cyan",
  },
];

export function OperationsDictionaryOverview() {
  const navigate = useNavigate();

  const handleCardClick = (card: DictionaryCard) => {
    navigate(card.route);
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Breadcrumb
            items={[
              { label: "Operations Panel", href: "/operations" },
              { label: "Dictionaries", isActive: true },
            ]}
          />
          <Title order={1} size="h2" fw={700}>
            Dictionary Management
          </Title>
          <Text c="dimmed" size="lg">
            Manage all dictionaries used throughout the CV Portal system
          </Text>
        </div>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
          {dictionaryCards.map((card) => (
            <Card
              key={card.id}
              p="xl"
              radius="md"
              withBorder
              shadow="sm"
              style={{ cursor: "pointer" }}
              onClick={() => handleCardClick(card)}
            >
              <Stack gap="md">
                <Group justify="space-between">
                  <ThemeIcon
                    size={48}
                    radius="md"
                    variant="light"
                    color={card.color}
                  >
                    <card.icon style={{ width: rem(24), height: rem(24) }} />
                  </ThemeIcon>
                </Group>

                <div>
                  <Text fw={600} size="lg" mb="xs">
                    {card.title}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {card.description}
                  </Text>
                </div>

                {card.itemCount && (
                  <Group>
                    <Badge variant="outline" color="gray">
                      {card.itemCount} items
                    </Badge>
                  </Group>
                )}

                <Group justify="flex-end" mt="md">
                  <Button
                    variant="filled"
                    color={card.color}
                    rightSection={<ArrowRightIcon style={{ width: rem(16) }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCardClick(card);
                    }}
                  >
                    {card.id === "roles" || card.id === "companies"
                      ? "View"
                      : "Manage"}
                  </Button>
                </Group>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>

        <Card withBorder p="lg" mt="xl">
          <Stack gap="md">
            <Title order={3} size="h4">
              Quick Actions
            </Title>
            <Text size="sm" c="dimmed">
              Common dictionary management tasks
            </Text>
            <Group>
              <Button
                variant="light"
                onClick={() =>
                  navigate("/operations/dictionaries/certificates")
                }
              >
                Add Certificate
              </Button>
              <Button
                variant="light"
                onClick={() =>
                  navigate("/operations/dictionaries/technologies")
                }
              >
                Manage Technologies
              </Button>
              <Button
                variant="light"
                onClick={() => navigate("/operations/dictionaries/education")}
              >
                Education Dictionaries
              </Button>
              <Button
                variant="light"
                onClick={() => navigate("/operations/dictionaries/languages")}
              >
                Language Dictionaries
              </Button>
              <Button
                variant="light"
                onClick={() => navigate("/operations/dictionaries/roles")}
              >
                View Roles & Positions
              </Button>
              <Button
                variant="light"
                onClick={() => navigate("/operations/dictionaries/companies")}
              >
                View Companies
              </Button>
              <Button
                variant="light"
                onClick={() =>
                  navigate("/operations/dictionaries/officelocations")
                }
              >
                Manage Office Locations
              </Button>
              <Button variant="light" disabled>
                Bulk Import (Coming Soon)
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
